import { IVideoFunctions } from 'services/ProductFormService'
import { IAsset,IFunctionQuestion } from 'types/assets'
import { IHotpoint } from 'types/products'
import { ISummaryProperties } from 'types/summary'

export const isEmptyAsset = (asset: Record<string, any>) => {
  if (!asset) {
    return true
  }

  if (!(typeof asset === 'object')) {
    return true
  }

  if (Object.keys(asset).length === 1 && Object.keys(asset)[0] === 'id') {
    return true
  } else if (
    Object.keys(asset).includes('imageUrl') &&
    !asset.imageUrl &&
    !isYouTubeUrl(asset.videoYoutubeId)
  ) {
    return true
  }

  return false
}

/** Intro section media: uploaded file URL and/or YouTube link (landing-page video asset pattern). */
export const hasIntroMedia = (intro: Record<string, any> | null | undefined): boolean =>
  Boolean(intro?.imageUrl) || isYouTubeUrl(intro?.videoYoutubeId)

/** YouTube intro — CSS-only facade (checkbox + label); no in-iframe play-button styling. */
export const renderIntroYoutubeMarkup = (
  intro: Record<string, any>,
  options?: {
    lazyPoster?: boolean
    isChunk?: boolean
    /** Haier2 — play on poster, pause while iframe is visible. */
    playPauseControl?: boolean
  }
): string => {
  const youtubeId = getYouTubeVideoId(intro?.videoYoutubeId)

  if (!youtubeId) return ''

  const toggleId = `intro-yt-${youtubeId}`
  const thumbUrl =
    getYouTubeThumbnailUrl(intro?.videoYoutubeId, 'maxresdefault') ||
    getYouTubeThumbnailUrl(intro?.videoYoutubeId, 'hqdefault') ||
    ''
  const lazyPoster = options?.lazyPoster && options?.isChunk
  const posterSrc =
    lazyPoster && thumbUrl
      ? '//f00.esfr.pl/si_upload/blank_gif/blank.gif'
      : thumbUrl
  const posterImg = posterSrc
    ? `<img class='intro-video__poster lazy' src='${posterSrc}'${
        lazyPoster && thumbUrl ? ` data-original='${thumbUrl}'` : ''
      } alt='' />`
    : ''

  const embedSrc = buildYouTubeEmbedSrc(youtubeId, {
    autoplay: 1,
    mute: 1,
    controls: 0,
  })

  const iframeTitle = intro.attributeAlt || 'YouTube video'
  const iframeAllow =
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'

  const iframeMarkup = `<iframe
            class='intro-video--iframe'
            referrerpolicy="strict-origin-when-cross-origin"
            src="${embedSrc}"
            title="${iframeTitle}"
            allow="${iframeAllow}"
            allowfullscreen
          ></iframe>`


  if (options?.playPauseControl) {
    return `<figure class='intro-video intro-video--youtube intro-video--youtube-playpause'>
          <input type="checkbox" id='${toggleId}' class='intro-video__toggle noSafari' aria-label="Play or pause video" />
          <label for='${toggleId}' class='intro-video__facade'>
            ${posterImg}
            <span class='sr-only'>${intro.attributeAlt || 'Play video'}</span>
          </label>
          ${iframeMarkup}
          <label for='${toggleId}' class='intro-video__control intro-video__control--pause'>
            <span class='intro-video__icon intro-video__icon--pause'>Pause video</span>
          </label>
          <label for='${toggleId}' class='intro-video__control intro-video__control--play'>
            <span class='intro-video__icon intro-video__icon--play'>Play video</span>
          </label>
          <figcaption id="introVideoAlt" class='sr-only'>${intro.attributeAlt || ''}</figcaption>
        </figure>`
  }

  return `<figure class='intro-video intro-video--youtube'>
          <input type="checkbox" id='${toggleId}' class='intro-video__toggle noSafari' aria-label="Play video" />
          <label for='${toggleId}' class='intro-video__facade'>
            ${posterImg}
            <span class='intro-video__play' aria-hidden="true">&nbsp;</span>
            <span class='sr-only'>${intro.attributeAlt || 'Puść lub zatrzymaj video'}</span>
          </label>
          ${iframeMarkup}
          <figcaption id="introVideoAlt" class='sr-only'>${intro.attributeAlt || ''}</figcaption>
        </figure>`
}

/** File intro video — CSS-only play/pause (checkbox swaps playing vs static video). */
export const renderIntroFileVideoMarkup = (
  intro: Record<string, any>,
  toggleId = 'intro-file-video'
): string => {
  const videoType = getVideoType(intro.imageFileName)
  const src = intro.imageUrl

  // Checkbox sibling-first — Safari Shadow DOM: label[for] + `:checked ~` (packshot pattern).
  return `<figure class='intro-video intro-video--file'>
          <input type="checkbox" id='${toggleId}' class='intro-video__toggle noSafari' aria-label="Puść lub zatrzymaj video" />
          <video muted autoplay loop playsinline aria-labelledby="introVideoAlt" class='intro-video--video intro-video--playing'>
            <source src='${src}' type='${videoType}'>
          </video>
          <video muted playsinline preload="metadata" aria-hidden="true" class='intro-video--video intro-video--paused'>
            <source src='${src}' type='${videoType}'>
          </video>
          <label for='${toggleId}' class='intro-video__control intro-video__control--pause'>
            <span class='intro-video__icon intro-video__icon--pause'>Zatrzymaj video</span>
          </label>
          <label for='${toggleId}' class='intro-video__control intro-video__control--play'>
            <span class='intro-video__icon intro-video__icon--play'>Puść video</span>
          </label>
          <figcaption id="introVideoAlt" class='sr-only'>${intro.attributeAlt || ''}</figcaption>
        </figure>`
}

export const renderIntroMediaMarkup = (
  intro: Record<string, any>,
  options?: {
    lazyImage?: boolean
    isChunk?: boolean
    /** RTV Euro chunk — blank.gif `src` + `data-original` URL. */
    useLazyPlaceholder?: boolean
    /** Haier2 — checkbox play/pause for intro file video and YouTube. */
    fileVideoPlayPause?: boolean
    playPauseControl?: boolean
  }
): string => {
  const playPauseControl =
    options?.playPauseControl ?? options?.fileVideoPlayPause

  if (intro.imageUrl) {
    if (isVideo(intro.imageFileName)) {
      if (playPauseControl) {
        return renderIntroFileVideoMarkup(intro)
      }

      return `<figure class='intro-video' tabindex="0">
                  <video muted autoplay loop playsinline aria-labelledby="introVideoAlt" class='intro-video--video'>
                    <source src='${intro.imageUrl}' type='${getVideoType(intro.imageFileName)}'>
                  </video>
                  <figcaption id="introVideoAlt" class='sr-only'>${intro.attributeAlt || ''}</figcaption>
                </figure>`
    }

    const lazy = options?.lazyImage
    const isChunk = options?.isChunk
    const usePlaceholder = Boolean(
      options?.useLazyPlaceholder ?? (lazy && isChunk)
    )

    return `<img
                  tabindex="0"
                  class='intro-image lazy'
                  src='${
                    usePlaceholder
                      ? '//f00.esfr.pl/si_upload/blank_gif/blank.gif'
                      : intro.imageUrl
                  }'
                  data-original='${intro.imageUrl}'
                  alt='${intro.attributeAlt || ''}'
                />`
  }

  return renderIntroYoutubeMarkup(intro, {
    lazyPoster: options?.lazyImage,
    isChunk: options?.isChunk,
    playPauseControl,
  })
}

export const isEmptyObject = (obj: Record<string, any>) => {
  if (!obj || !(typeof obj === 'object')) {
    return true
  }

  return Object.keys(obj).length === 0
}

export const isObjectEmptyStrings = (obj: Record<string, any>) =>
  obj.filter((el: { id: string }) => el.id !== '').length <= 0

export const isVideo = (imageFileName: string) => {
  const fileExtension = imageFileName?.split('.')?.pop()

  return (
    fileExtension?.toLowerCase() === 'mp4' ||
    fileExtension?.toLowerCase() === 'webm'
  )
}

export const getVideoType = (imageFileName: string) => {
  const fileExtension = imageFileName?.split('.')?.pop()

  return `video/${fileExtension}`
}

/** YouTube video IDs are exactly 11 chars: [a-zA-Z0-9_-]. */
const YOUTUBE_ID_REGEX = /^[a-zA-Z0-9_-]{11}$/

/** Extract YouTube video ID from full URL or return the string if it's already a bare video ID. */
export function getYouTubeVideoId(url: string | null | undefined): string | null {
  if (url == null || typeof url !== 'string') return null
  const trimmed = String(url).trim()

  if (!trimmed) return null
  // Bare video ID (e.g. "dQw4w9WgXcQ")
  if (YOUTUBE_ID_REGEX.test(trimmed)) return trimmed
  // youtu.be/VIDEO_ID (optional query/hash)
  const shortMatch = trimmed.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/)

  if (shortMatch) return shortMatch[1]
  // youtube.com/watch?v=VIDEO_ID or embed/VIDEO_ID (match 11-char id after v= or embed/)
  const watchMatch = trimmed.match(/(?:[?&]v=|\/embed\/)([a-zA-Z0-9_-]{11})/)

  if (watchMatch) return watchMatch[1]
  if (/youtube\.com/.test(trimmed) && /[?&]v=/.test(trimmed)) {
    const vMatch = trimmed.match(/[?&]v=([a-zA-Z0-9_-]{11})/)

    if (vMatch) return vMatch[1]
  }

  return null
}

/** Minimal embed chrome — internal YT DOM is not styleable cross-origin; use params + wrapper crop in CSS. */
export function buildYouTubeEmbedSrc(
  videoId: string,
  params?: Record<string, string | number | boolean>
): string {
  const defaults: Record<string, string> = {
    modestbranding: '1',
    rel: '0',
    iv_load_policy: '3',
    playsinline: '1',
    disablekb: '1',
    fs: '0',
    controls: '0',
  }
  const merged = { ...defaults, ...params }
  const search = new URLSearchParams(
    Object.fromEntries(
      Object.entries(merged).map(([key, value]) => [key, String(value)])
    )
  ).toString()

  return `https://www.youtube-nocookie.com/embed/${videoId}?${search}`
}

/** YouTube thumbnail sizes: default 120x90, mqdefault 320x180, hqdefault 480x360, sddefault 640x480, maxresdefault 1280x720. */
export function getYouTubeThumbnailUrl(
  url: string | null | undefined,
  size: 'default' | 'mqdefault' | 'hqdefault' | 'sddefault' | 'maxresdefault' = 'hqdefault'
): string | null {
  const videoId = getYouTubeVideoId(url)

  if (!videoId) return null

  return `https://img.youtube.com/vi/${videoId}/${size}.jpg`
}

/** True if value is a YouTube URL or a bare video ID. */
export function isYouTubeUrl(url: string | null | undefined): boolean {
  return getYouTubeVideoId(url) != null
}

/**
 * True when the file name or URL refers to a video (file extension, URL path, data URL, or YouTube).
 * Used to hide image editor actions — the canvas editor only supports raster images.
 */
export function isVideoAssetUrlOrName(
  fileNameOrUrl: string | null | undefined
): boolean {
  if (fileNameOrUrl == null || fileNameOrUrl === '') return false
  const s = String(fileNameOrUrl).trim()

  if (isYouTubeUrl(s)) return true
  if (/^data:video\//i.test(s)) return true
  if (/\.(mp4|webm)(\?[^#]*)?(#|$)/i.test(s)) return true

  return isVideo(s)
}

/** MIME for LP/product video previews (file name and/or URL with optional query string). */
export function getVideoMimeTypeFromUrlOrFileName(
  url: string | null | undefined,
  fileName: string | null | undefined
): string {
  if (fileName && isVideo(fileName)) return getVideoType(fileName)
  if (url && isVideoAssetUrlOrName(url)) {
    const clean = url.split('?')[0].split('#')[0]
    const ext = clean.split('.').pop()?.toLowerCase()

    if (ext === 'webm' || ext === 'mp4') return `video/${ext}`
  }

  return 'video/mp4'
}

/** True when hover/cell preview should use a &lt;video&gt; element (not YouTube thumbnail). */
export function isLpFileVideoPreview(
  url: string | null | undefined,
  fileName: string | null | undefined,
  isYouTubePreview = false
): boolean {
  if (isYouTubePreview) return false

  return isVideoAssetUrlOrName(url) || isVideoAssetUrlOrName(fileName)
}

/** Return the YouTube watch URL. Accepts full URL or bare video ID. */
export function getYouTubeWatchUrl(urlOrId: string | null | undefined): string | null {
  const videoId = getYouTubeVideoId(urlOrId)

  if (!videoId) return null

  return `https://www.youtube.com/watch?v=${videoId}`
}

export type AssetPreviewInfo = {
  url: string | null
  fileName: string | null
  isYouTubePreview: boolean
  linkUrl: string | null
}

/** Resolve preview URL for tables / tooltips (image, file video, YouTube, LP hotpoints/video). */
export function getAssetPreviewInfo(row: object): AssetPreviewInfo {
  // Interfaces like IAsset lack an index signature, so accept object and narrow fields here.
  const data = row as Record<string, unknown>
  const imageUrl =
    typeof data.imageUrl === 'string' && data.imageUrl.trim()
      ? data.imageUrl.trim()
      : null
  const largeImageUrl =
    typeof data.largeImageUrl === 'string' && data.largeImageUrl.trim()
      ? data.largeImageUrl.trim()
      : null
  const hotpointsUrl =
    typeof data.hotpointsUrl === 'string' && data.hotpointsUrl.trim()
      ? data.hotpointsUrl.trim()
      : null
  const hotpointsMobileUrl =
    typeof data.hotpointsMobileUrl === 'string' && data.hotpointsMobileUrl.trim()
      ? data.hotpointsMobileUrl.trim()
      : null
  const videoUrl =
    typeof data.videoUrl === 'string' && data.videoUrl.trim()
      ? data.videoUrl.trim()
      : null
  const videoYoutubeId =
    typeof data.videoYoutubeId === 'string' && data.videoYoutubeId.trim()
      ? data.videoYoutubeId.trim()
      : null
  const packshotUrl =
    typeof data.packshotUrl === 'string' && data.packshotUrl.trim()
      ? data.packshotUrl.trim()
      : null
  const openedPackshotUrl =
    typeof data.openedPackshotUrl === 'string' && data.openedPackshotUrl.trim()
      ? data.openedPackshotUrl.trim()
      : null
  const landingPageFileUrl =
    typeof data.landingPageFileUrl === 'string' &&
    data.landingPageFileUrl.trim()
      ? data.landingPageFileUrl.trim()
      : null
  const sourceGraphicFileUrl =
    typeof data.sourceGraphicFileUrl === 'string' &&
    data.sourceGraphicFileUrl.trim()
      ? data.sourceGraphicFileUrl.trim()
      : null

  const fileNameFromRow = (): string | null => {
    if (
      imageUrl &&
      typeof data.imageFileName === 'string' &&
      data.imageFileName
    ) {
      return data.imageFileName
    }

    if (
      hotpointsUrl &&
      typeof data.hotpointsFileName === 'string' &&
      data.hotpointsFileName
    ) {
      return data.hotpointsFileName
    }

    if (
      hotpointsMobileUrl &&
      typeof data.hotpointsMobileFileName === 'string' &&
      data.hotpointsMobileFileName
    ) {
      return data.hotpointsMobileFileName
    }

    if (
      videoUrl &&
      typeof data.videoFileName === 'string' &&
      data.videoFileName
    ) {
      return data.videoFileName
    }

    if (
      packshotUrl &&
      typeof data.packshotFileName === 'string' &&
      data.packshotFileName
    ) {
      return data.packshotFileName
    }

    if (
      openedPackshotUrl &&
      typeof data.openedPackshotFileName === 'string' &&
      data.openedPackshotFileName
    ) {
      return data.openedPackshotFileName
    }

    if (
      landingPageFileUrl &&
      typeof data.landingPageFileFileName === 'string' &&
      data.landingPageFileFileName
    ) {
      return data.landingPageFileFileName
    }

    if (
      sourceGraphicFileUrl &&
      typeof data.sourceGraphicFileFileName === 'string' &&
      data.sourceGraphicFileFileName
    ) {
      return data.sourceGraphicFileFileName
    }

    return null
  }

  const directUrl =
    imageUrl ||
    hotpointsUrl ||
    hotpointsMobileUrl ||
    videoUrl ||
    packshotUrl ||
    openedPackshotUrl ||
    largeImageUrl ||
    landingPageFileUrl ||
    sourceGraphicFileUrl

  if (directUrl) {
    if (isYouTubeUrl(directUrl)) {
      return {
        url: getYouTubeThumbnailUrl(directUrl),
        fileName: null,
        isYouTubePreview: true,
        linkUrl: getYouTubeWatchUrl(directUrl) ?? directUrl
      }
    }

    return {
      url: directUrl,
      fileName: fileNameFromRow(),
      isYouTubePreview: false,
      linkUrl: directUrl
    }
  }

  if (videoYoutubeId && isYouTubeUrl(videoYoutubeId)) {
    const thumb = getYouTubeThumbnailUrl(videoYoutubeId)

    return {
      url: thumb,
      fileName: null,
      isYouTubePreview: true,
      linkUrl: getYouTubeWatchUrl(videoYoutubeId) ?? videoYoutubeId
    }
  }

  return {
    url: null,
    fileName: null,
    isYouTubePreview: false,
    linkUrl: null
  }
}

/** Product-form asset dropdown tooltip preview (image, short file video, or YouTube thumbnail). */
export function getAssetTooltipPreview(asset: {
  imageUrl?: string | null
  imageFileName?: string | null
  videoYoutubeId?: string | null
  defaultDescription?: string | null
}): { image?: string; desc?: string; imageFileName?: string } {
  const desc = asset.defaultDescription ?? undefined
  const imageUrl = asset.imageUrl?.trim()

  if (imageUrl) {
    return {
      image: imageUrl,
      desc,
      imageFileName: asset.imageFileName ?? undefined
    }
  }

  const youtubeId = asset.videoYoutubeId?.trim()

  if (youtubeId && isYouTubeUrl(youtubeId)) {
    const thumb = getYouTubeThumbnailUrl(youtubeId)

    return thumb ? { image: thumb, desc } : { desc }
  }

  return { desc }
}

export const currentProductToCompare = (product: any) =>
  product?.comparison?.comparedProducts?.find(
    (item: any) => item.product === null
  )

export const notCurrentProductsToCompare = (product: any) =>
  product?.comparison?.comparedProducts?.filter(
    (item: any) => item.product !== null
  )

export const hotpointsProperties = (product: any) =>
  (JSON.parse(product?.hotpointsProperties || '[]') as IHotpoint[]) || []

export const hotpointsMobileProperties = (product: any) =>
  (JSON.parse(product?.hotpointsMobileProperties || '[]') as IHotpoint[]) || []

export const hotpointsMobile = (product: any) =>
  product?.hotpointsMobileUrl || product?.hotpointsUrl

export const summaryProperties = (product: any) =>
  (JSON.parse(product?.summaryProperties || '[]') as ISummaryProperties[]) || []

export const videoFunctions = (product: any) =>
  (JSON.parse(product?.videoFunctions || '[]') as IVideoFunctions[]) || []

export const isEmptyText = (text?: string | null) =>
  text == null || String(text).trim().length <= 0

export const defaultQuestionMapper = (fun: IAsset): IFunctionQuestion => {
  return {
    header: fun.name,
    content: fun.defaultDescription || '',
    largeImageUrl: fun.largeImageUrl,
    imageUrl: fun.imageUrl,
    alt: fun.attributeAlt || fun.attributeAltMobile
  }
}

export const getQuestionsFromFunctions = (
  start: number = 15,
  end: number = 20,
  functions: IAsset[],
  funMapper: (fun: IAsset) => IFunctionQuestion = defaultQuestionMapper,
): IFunctionQuestion[] => {
  if (!functions) {
    return []
  }

  return functions
    .slice(start, end)
    .filter((fun: IAsset) => fun?.name)
    .map(funMapper)
}

export const isIframe = (fileName: string): boolean => {
  const extension = fileName?.split('.')?.pop();

  return extension?.toLowerCase() === 'html'
}

export const isEven = (index: number): boolean => {
  return index % 2 === 0;
}

const VALID_GROUPED_ASSET_KEYS = new Set([
  'banner',
  'first_lead',
  'features',
  'intro',
  'second_lead',
  'attributes',
  'functions',
  'icon_functions',
  'photos',
  'slider',
  'accessories',
  'icons'
])

type AnyRecord = Record<string, unknown>

function isRecord(value: unknown): value is AnyRecord {
  return Boolean(value && typeof value === 'object')
}

function getSortedProductAssetRows(
  product: Record<string, unknown> | null | undefined
): unknown[] {
  const rows = product?.productAssets

  if (!Array.isArray(rows)) {
    return []
  }

  return [...rows].sort(
    (a, b) =>
      ((a as { orderInProduct?: number }).orderInProduct ?? 0) -
      ((b as { orderInProduct?: number }).orderInProduct ?? 0)
  )
}

function getRowAsset(row: unknown): AnyRecord | null {
  if (!isRecord(row) || !('asset' in row)) {
    return null
  }

  const asset = (row as { asset?: AnyRecord }).asset

  return isRecord(asset) ? asset : null
}

function hasImageAsset(
  asset: AnyRecord | null | undefined
): asset is AnyRecord & { imageUrl: unknown } {
  return Boolean(
    asset && !isEmptyAsset(asset as Record<string, any>) && asset.imageUrl
  )
}

function getAssetArrayByKeys(
  assets: Record<string, unknown> | null | undefined,
  keys: string[]
): unknown[] {
  if (!isRecord(assets)) {
    return []
  }

  for (const key of keys) {
    const value = assets[key]

    if (Array.isArray(value)) {
      return value
    }
  }

  return []
}

/** Resolve `{ id }` / nested `asset.id` / UUID string from a product asset slot entry. */
export function extractAssetSlotReferenceId(item: unknown): string {
  if (typeof item === 'string') {
    const id = item.trim()

    return id === 'undefined' || id === 'null' ? '' : id
  }

  if (!isRecord(item)) {
    return ''
  }

  const nestedAsset = item.asset

  if (isRecord(nestedAsset) && 'id' in nestedAsset) {
    const nestedId = String(nestedAsset.id ?? '').trim()

    if (nestedId && nestedId !== 'undefined' && nestedId !== 'null') {
      return nestedId
    }
  }

  if ('id' in item) {
    const id = String(item.id ?? '').trim()

    if (id && id !== 'undefined' && id !== 'null') {
      return id
    }
  }

  return ''
}

function resolveOrderInProductSlotIndex(
  orderInProduct: number | undefined,
  slotCount: number,
  ordersForType: number[]
): number | undefined {
  if (typeof orderInProduct !== 'number' || !Number.isFinite(orderInProduct)) {
    return undefined
  }

  const useOneBased =
    ordersForType.length > 0 &&
    ordersForType.every(order => order >= 1) &&
    !ordersForType.includes(0)
  const index = useOneBased ? orderInProduct - 1 : orderInProduct

  if (index < 0 || index >= slotCount) {
    return undefined
  }

  return index
}

/** Build positioned `{ id }` slots from `productAssets[]` rows for a form field. */
export function buildAssetSlotsFromProductAssetRows(
  product: Record<string, unknown> | null | undefined,
  formFieldKey: string,
  slotCount: number
): { id: string }[] {
  const slots = Array.from({ length: slotCount }, () => ({ id: '' }))
  const rows = getSortedProductAssetRows(product)
  const rowsForType: {
    asset: AnyRecord
    order?: number
  }[] = []

  for (const row of rows) {
    const asset = getRowAsset(row)

    if (!asset) continue

    const typeName = String(
      asset.assetTypeName ??
        (asset.assetType as { name?: string } | undefined)?.name ??
        ''
    )

    if (assetTypeNameToGroupedKey(typeName) !== formFieldKey) continue

    rowsForType.push({
      asset,
      order: (row as { orderInProduct?: number }).orderInProduct
    })
  }

  const orders = rowsForType
    .map(entry => entry.order)
    .filter((order): order is number => typeof order === 'number')

  let nextSequentialIndex = 0

  for (const entry of rowsForType) {
    const assetId = extractAssetSlotReferenceId(entry.asset)

    if (!assetId) continue

    const positionedIndex = resolveOrderInProductSlotIndex(
      entry.order,
      slotCount,
      orders
    )

    let targetIndex = positionedIndex

    if (targetIndex === undefined) {
      while (
        nextSequentialIndex < slotCount &&
        Boolean(slots[nextSequentialIndex]?.id)
      ) {
        nextSequentialIndex += 1
      }
      targetIndex =
        nextSequentialIndex < slotCount ? nextSequentialIndex : undefined
      if (targetIndex !== undefined) {
        nextSequentialIndex += 1
      }
    }

    if (targetIndex === undefined) continue
    if (!slots[targetIndex].id) {
      slots[targetIndex] = { id: assetId }
    }
  }

  return slots
}

export function getGroupedAssetArrayForFormField(
  assets: Record<string, unknown> | null | undefined,
  formFieldKey: string,
  apiFieldName?: string
): unknown[] {
  const keys = [formFieldKey]

  if (apiFieldName && apiFieldName !== formFieldKey) {
    keys.push(apiFieldName)
  }

  return getAssetArrayByKeys(assets, keys)
}

/** Map API asset type label → grouped `assets` key using strict valid names only. */
export function assetTypeNameToGroupedKey(name: string): string {
  const slug = name.replace(/\s/g, '_').toLowerCase()

  return VALID_GROUPED_ASSET_KEYS.has(slug) ? slug : ''
}

/**
 * Build `assets.{photos,icons,...}` from GET `productAssets[]` when API omits grouped `assets`.
 */
export function assetReferenceArrayHasIds(value: unknown): boolean {
  if (!Array.isArray(value)) {
    return false
  }

  return value.some(item => Boolean(extractAssetSlotReferenceId(item)))
}

/** Prefer grouped API slot refs when they carry ids; otherwise keep `productAssets` rows. */
export function mergeGroupedProductAssets(
  fromProductAssets: Record<string, unknown[]>,
  fromApiAssets: Record<string, unknown>
): Record<string, unknown[]> {
  const merged: Record<string, unknown[]> = { ...fromProductAssets }

  for (const [key, apiValue] of Object.entries(fromApiAssets)) {
    if (!Array.isArray(apiValue)) {
      continue
    }

    const existing = merged[key]
    const apiHasIds = assetReferenceArrayHasIds(apiValue)
    const existingHasIds = assetReferenceArrayHasIds(existing)

    if (!apiHasIds && existingHasIds) {
      continue
    }

    if (apiHasIds && !existingHasIds) {
      merged[key] = apiValue
      continue
    }

    if (!apiHasIds && !existingHasIds) {
      merged[key] = apiValue.length > 0 ? (apiValue) : existing ?? []
      continue
    }

    const maxLength = Math.max(apiValue.length, existing?.length ?? 0)
    const combined: unknown[] = []

    for (let index = 0; index < maxLength; index += 1) {
      const apiItem = apiValue[index]
      const existingItem = existing?.[index]
      const apiId = extractAssetSlotReferenceId(apiItem)
      const existingId = extractAssetSlotReferenceId(existingItem)

      combined[index] = apiId ? apiItem : existingId ? existingItem : apiItem ?? existingItem
    }
    merged[key] = combined
  }

  return merged
}

export function groupProductAssetsByAssetType(
  product: Record<string, unknown> | null | undefined
): Record<string, unknown[]> {
  const grouped: Record<string, unknown[]> = {}
  const rows = getSortedProductAssetRows(product)

  for (const row of rows) {
    const asset = getRowAsset(row)

    if (!asset) continue

    const typeName = String(
      asset.assetTypeName ??
        (asset.assetType as { name?: string } | undefined)?.name ??
        ''
    )

    if (!typeName) continue

    const key = assetTypeNameToGroupedKey(typeName)

    if (!key) continue

    if (!grouped[key]) grouped[key] = []
    grouped[key].push(asset)
  }

  return grouped
}

/** API / `transformObjectKeys` spellings for slider slots (`Slider` / `slider`). */
export function getSliderAssetArray(
  assets: Record<string, unknown> | null | undefined
): unknown[] {
  return getAssetArrayByKeys(assets, ['slider', 'Slider'])
}

/** API / `transformObjectKeys` spellings for photos slots (`Photos` / `photos`). */
export function getPhotosAssetArray(
  assets: Record<string, unknown> | null | undefined
): unknown[] {
  return getAssetArrayByKeys(assets, ['photos', 'Photos'])
}

export function assetSlotHasImage(slot: unknown): boolean {
  return Boolean(
    slot &&
      typeof slot === 'object' &&
      (slot as { imageUrl?: string }).imageUrl
  )
}

export function photoAssetSlotsHaveImages(slots: unknown[] | undefined): boolean {
  if (!Array.isArray(slots)) {
    return false
  }

  return slots.some(assetSlotHasImage)
}

/** Resolve photo slots (`{ id }` or full asset) using the product asset index. */
export function resolvePhotoAssetSlots(
  slots: unknown[],
  assetsById: Map<string, Record<string, unknown>>
): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = []

  for (const slot of slots) {
    if (!isRecord(slot)) {
      continue
    }

    if (hasImageAsset(slot)) {
      const row = slot as Record<string, unknown>

      result.push(row)
      continue
    }

    const id = slot.id ? String(slot.id) : ''

    if (!id) {
      continue
    }

    const resolved = assetsById.get(id)

    if (hasImageAsset(resolved)) {
      result.push(resolved)
    }
  }

  return result
}

/**
 * Photos carousel (`Haier2Photos`) — `assets.photos`, then `productAssets` (Photos type).
 */
export function getHaier2CarouselPhotosFromProduct(
  product: Record<string, unknown> | null | undefined
): Record<string, unknown>[] {
  if (!product) {
    return []
  }

  const assetsById = collectProductAssetsById(product)

  const fromPhotos = resolvePhotoAssetSlots(
    getPhotosAssetArray(product.assets as Record<string, unknown> | undefined),
    assetsById
  )

  if (fromPhotos.length > 0) {
    return fromPhotos
  }

  return getPhotosFromProductAssets(product)
}

/**
 * Lifestyle grid (`Haier2Photos2`) — `assets.slider` / `Slider` slots (form Slider section).
 * Haier: each slot `{ id }` references a Photos asset; images resolved via {@link collectProductAssetsById}.
 */
export function getHaier2SliderPhotosFromProduct(
  product: Record<string, unknown> | null | undefined
): Record<string, unknown>[] {
  if (!product) {
    return []
  }

  const assetsById = collectProductAssetsById(product)

  return resolvePhotoAssetSlots(
    getSliderAssetArray(product.assets as Record<string, unknown> | undefined),
    assetsById
  )
}

/** `productAssets[]` rows for asset type Photos, in `orderInProduct`. */
export function getPhotosFromProductAssets(
  product: Record<string, unknown> | null | undefined
): Record<string, unknown>[] {
  return getSortedProductAssetRows(product)
    .map(row => {
      const asset = getRowAsset(row)

      return { row, asset }
    })
    .filter(
      (entry): entry is { row: unknown; asset: Record<string, unknown> } =>
        Boolean(
          entry.asset &&
            entry.asset.assetTypeName === 'Photos' &&
            hasImageAsset(entry.asset)
        )
    )
    .map(entry => entry.asset)
}

/** API / `transformObjectKeys` spellings for dimensions accessory icon slots. */
export function getDimensionsAccesoriesAssetArray(
  assets: Record<string, unknown> | null | undefined
): unknown[] {
  if (!assets || typeof assets !== 'object') {
    return []
  }

  const raw =
    assets.dimensionsAccesories ??
    assets.dimensionsaccesories ??
    assets.dimensions_accesories

  return Array.isArray(raw) ? raw : []
}

/** Index all asset rows on a product by `id` (icons, photos, `productAssets`, etc.). */
export function collectProductAssetsById(
  product: Record<string, unknown> | null | undefined
): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>()

  const add = (item: unknown) => {
    if (!item || typeof item !== 'object' || !('id' in item)) {
      return
    }

    const row = item as Record<string, unknown>
    const id = String(row.id ?? '')

    if (!id) {
      return
    }

    const existing = map.get(id)

    if (!existing || (row.imageUrl && !existing.imageUrl)) {
      map.set(id, row)
    }
  }

  const assets = product?.assets

  if (assets && typeof assets === 'object') {
    for (const value of Object.values(assets as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        value.forEach(add)
      }
    }
  }

  const productAssets = product?.productAssets

  if (Array.isArray(productAssets)) {
    for (const entry of productAssets) {
      if (entry && typeof entry === 'object' && 'asset' in entry) {
        add((entry as { asset?: unknown }).asset)
      }
    }
  }

  return map
}

export function buildPreviewProductAssets(
  readableAssets: Record<string, unknown> | null | undefined
) {
  return {
    banner: readableAssets?.banner || [{ id: '' }],
    first_lead: readableAssets?.first_lead || [{ id: '' }],
    features: readableAssets?.features || new Array(4).fill({ id: '' }),
    intro: readableAssets?.intro || [{ id: '' }],
    second_lead: readableAssets?.second_lead || [{ id: '' }],
    attributes: readableAssets?.attributes || new Array(4).fill({ id: '' }),
    functions: readableAssets?.functions || new Array(21).fill({ id: '' }),
    icon_functions:
      readableAssets?.icon_functions || new Array(8).fill({ id: '' }),
    photos: readableAssets?.photos || new Array(6).fill({ id: '' }),
    slider: readableAssets?.slider || new Array(9).fill({ id: '' }),
    accessories: readableAssets?.accessories || new Array(10).fill({ id: '' }),
    icons: readableAssets?.icons || new Array(8).fill({ id: '' }),
    dimensionsAccesories: getDimensionsAccesoriesAssetArray(readableAssets)
  }
}

